import { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  FRAMEWORK_DISCLOSURE_COPY,
  NAV_LABELS,
  OWASP_MAPPING_LABELS,
  STATUS_LABELS,
  normalizeSecurityStatus,
} from '@/lib/vocabulary/securityTerminology';
import type { DemoDomain } from '@/demo/demoFixtures';
import { formatTimestampSafe } from '@/lib/utils/formatTimestamp';

const badgeMeta = (badge: 'covered' | 'partial' | 'action' | 'planned', labelOverride?: string) => {
  switch (badge) {
    case 'covered':
      return { label: STATUS_LABELS.covered, variant: 'success' as const };
    case 'action':
      return { label: STATUS_LABELS.actionRequired, variant: 'danger' as const };
    case 'planned':
      return { label: STATUS_LABELS.monitoringPlanned, variant: 'neutral' as const };
    default:
      return { label: labelOverride ?? STATUS_LABELS.needsHost, variant: 'warning' as const };
  }
};

const bucketPriority: Record<'covered' | 'managed' | 'needs_host' | 'action' | 'planned', number> = {
  action: 4,
  needs_host: 3,
  managed: 2,
  covered: 1,
  planned: 0,
};

const bucketToBadge = (bucket: 'covered' | 'managed' | 'needs_host' | 'action' | 'planned') => {
  if (bucket === 'action') return 'action';
  if (bucket === 'covered') return 'covered';
  if (bucket === 'planned') return 'planned';
  return 'partial';
};

const bucketToLabel = (bucket: 'covered' | 'managed' | 'needs_host' | 'action' | 'planned') => {
  switch (bucket) {
    case 'managed':
      return STATUS_LABELS.managed;
    case 'needs_host':
      return STATUS_LABELS.needsHost;
    case 'action':
      return STATUS_LABELS.actionRequired;
    case 'covered':
      return STATUS_LABELS.covered;
    default:
      return STATUS_LABELS.monitoringPlanned;
  }
};

const GROUPS = [
  { id: 'account_access', title: 'Identity & Access', owaspIds: ['A01', 'A07'] },
  { id: 'updates_integrity', title: 'Updates & Integrity Protection', owaspIds: ['A06', 'A08'] },
  { id: 'application_hardening', title: 'Application Hardening', owaspIds: ['A02', 'A03', 'A05'] },
  { id: 'network_request', title: 'Network & Request Protection', owaspIds: ['A10'] },
  { id: 'visibility_evidence', title: 'Visibility & Evidence', owaspIds: ['A09'] },
];

const formatControlName = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

export default function DemoDomainCoverage() {
  const { domain } = useOutletContext<{ domain: DemoDomain }>();

  const grouped = useMemo(() => {
    return GROUPS.map((group) => {
      const categories = domain.owasp.filter((category) => group.owaspIds.includes(category.id));
      const controls = categories.flatMap((category) => category.controls.map((control) => ({
        ...control,
        owasp_id: category.id,
      })));

      let groupBucket: 'covered' | 'managed' | 'needs_host' | 'action' | 'planned' = 'planned';
      let lastChecked = '';
      let enforcementAvailable = 0;
      let enforcementActive = 0;

      controls.forEach((control) => {
        const normalized = normalizeSecurityStatus({
          status: control.status,
          enforced: control.enforced,
          hasSignals: true,
          enforcementAvailable: Boolean(control.enforcement_available),
        });
        if (bucketPriority[normalized.bucket] > bucketPriority[groupBucket]) {
          groupBucket = normalized.bucket;
        }

        if (control.last_checked_at && control.last_checked_at > lastChecked) {
          lastChecked = control.last_checked_at;
        }

        if (control.enforcement_available) {
          enforcementAvailable += 1;
          if (control.enforced) enforcementActive += 1;
        }
      });

      return {
        ...group,
        controls,
        groupStatus: bucketToBadge(groupBucket),
        groupBucket,
        groupLabel: bucketToLabel(groupBucket),
        lastChecked,
        monitoringCount: controls.length,
        enforcementAvailable,
        enforcementActive,
      };
    });
  }, [domain]);

  const overallBucket = useMemo((): 'covered' | 'managed' | 'needs_host' | 'action' | 'planned' => {
    const buckets = grouped.map((group) => group.groupBucket);
    return buckets.reduce((current, bucket) => (
      bucketPriority[bucket] > bucketPriority[current] ? bucket : current
    ), 'planned' as const);
  }, [grouped]);

  const overallBadge = badgeMeta(bucketToBadge(overallBucket), bucketToLabel(overallBucket));

  return (
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle>{NAV_LABELS.protectionCoverage}</CardTitle>
        <CardDescription>{FRAMEWORK_DISCLOSURE_COPY}</CardDescription>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Security Health</span>
          <Badge variant={overallBadge.variant}>{overallBadge.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Accordion type="multiple" className="space-y-2">
          {grouped.map((group) => {
            const badge = badgeMeta(group.groupStatus as 'covered' | 'partial' | 'action' | 'planned', group.groupLabel);
            return (
              <AccordionItem key={group.id} value={group.id} className="border border-border/60 rounded-md px-3">
                <AccordionTrigger className="flex items-center justify-between text-sm">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">{group.title}</span>
                    <span className="text-xs text-muted-foreground">
                      Last checked {group.lastChecked ? formatTimestampSafe(group.lastChecked) : '—'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    <span className="text-xs text-muted-foreground">
                      Monitoring: {group.monitoringCount} · Enforcement available: {group.enforcementAvailable} · Active: {group.enforcementActive}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  {group.controls.length === 0 ? (
                    <div className="text-xs text-muted-foreground">{STATUS_LABELS.monitoringPlanned}.</div>
                  ) : (
                    <div className="space-y-3">
                      {group.controls.map((control) => {
                        const normalized = normalizeSecurityStatus({
                          status: control.status,
                          enforced: control.enforced,
                          hasSignals: true,
                          enforcementAvailable: Boolean(control.enforcement_available),
                        });
                        const controlBadge = badgeMeta(normalized.badge, normalized.text);
                        return (
                          <div key={control.control_key} className="rounded-md border border-border/50 bg-muted/20 p-3">
                            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium">{formatControlName(control.control_key)}</span>
                                  <Badge variant={controlBadge.variant}>{controlBadge.label}</Badge>
                                  {normalized.why && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button type="button" className="text-xs text-muted-foreground underline">
                                          Why
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-fit max-w-xs px-3 py-2 text-xs">
                                        <div className="font-medium">Why</div>
                                        <div className="text-muted-foreground">{normalized.why}</div>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                  {control.enforcement_available && (
                                    <Badge variant={control.enforced ? 'success' : 'neutral'}>
                                      {control.enforced ? 'Enforced' : 'Available'}
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground">Recommended: {control.recommended_action}</div>
                                {control.evidence && (
                                  <div className="text-xs text-muted-foreground">Evidence: {control.evidence}</div>
                                )}
                                <div className="text-xs text-muted-foreground">Last checked: {formatTimestampSafe(control.last_checked_at)}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      <details className="text-xs text-muted-foreground">
                        <summary className="cursor-pointer">{OWASP_MAPPING_LABELS.technicalHeading}</summary>
                        <div className="mt-2 space-y-1">
                          <div>{OWASP_MAPPING_LABELS.internalOnly}</div>
                          <div>Mapped categories: {group.owaspIds.join(', ') || '—'}</div>
                        </div>
                      </details>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </CardContent>
    </Card>
  );
}
